/**
 * useSecureLinks Hook
 * Secure link CRUD operations with React Query
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client';
import { createQueryKeys } from './queryKeys';
import type { SecureLink, Policy, PagedCollection } from '../types/api';

// ============================================================================
// Query Keys
// ============================================================================

const base = createQueryKeys('secure-links');
export const secureLinkKeys = {
  ...base,
  list: (params: SecureLinkListParams) => [...base.lists(), params] as const,
};

// ============================================================================
// Types
// ============================================================================

export interface SecureLinkListParams {
  page?: number;
  limit?: number;
  status?: 'active' | 'expired' | 'used';
  policyId?: string;
}

export type SecureLinkListResponse = PagedCollection<SecureLink>;

export interface CreateSecureLinkRequest {
  policyId: string;
  expiresInDays?: number;
  findingId?: string;
}

export interface CreateFindingLinkRequest {
  policyId: string;
  findingId: string;
  expiresInDays?: number;
}

// ============================================================================
// API Extensions
// ============================================================================

// API helper functions using the actual API client
const secureLinkApi = {
  list: async (params?: SecureLinkListParams): Promise<SecureLinkListResponse> => {
    const queryParams: Record<string, string | number | boolean | undefined> = {
      page: params?.page,
      limit: params?.limit,
      status: params?.status,
      policyId: params?.policyId,
    };

    return api.secureLinks.list(queryParams);
  },

  create: async (data: CreateSecureLinkRequest): Promise<SecureLink> => {
    return api.secureLinks.create(data);
  },

  revoke: async (uuid: string): Promise<void> => {
    return api.secureLinks.revoke(uuid);
  },
};

// ============================================================================
// Hooks
// ============================================================================

/**
 * Fetch paginated list of secure links
 */
export function useSecureLinks(params: SecureLinkListParams = {}) {
  return useQuery({
    queryKey: secureLinkKeys.list(params),
    queryFn: () => secureLinkApi.list(params),
  });
}

/**
 * Fetch active secure links
 */
export function useActiveSecureLinks(params: Omit<SecureLinkListParams, 'status'> = {}) {
  return useSecureLinks({ ...params, status: 'active' });
}

/**
 * Validate a secure link by UUID
 */
export function useValidateLink(uuid: string | undefined) {
  return useQuery({
    queryKey: secureLinkKeys.detail(uuid!),
    queryFn: () => api.uploads.validateLink(uuid!),
    enabled: Boolean(uuid),
  });
}

/**
 * Create a new secure link
 */
export function useCreateSecureLink() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateSecureLinkRequest) => secureLinkApi.create(data),
    onSuccess: () => {
      // Invalidate and refetch secure link lists
      queryClient.invalidateQueries({ queryKey: secureLinkKeys.lists() });
    },
  });
}

/**
 * Create a finding-scoped inspection link
 */
export function useCreateFindingLink() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateFindingLinkRequest) =>
      secureLinkApi.create({
        policyId: data.policyId,
        findingId: data.findingId,
        expiresInDays: data.expiresInDays ?? 7,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: secureLinkKeys.lists() });
    },
  });
}

/**
 * Poll for the status of a finding-scoped link (pending → accessed → completed)
 */
export function useFindingLinkStatus(uuid: string | undefined) {
  return useQuery({
    queryKey: [...secureLinkKeys.all, 'finding-link-status', uuid] as const,
    queryFn: () => api.uploads.validateLink(uuid!),
    enabled: Boolean(uuid),
    refetchInterval: uuid ? 10_000 : false, // Poll every 10s when active
  });
}

/**
 * Revoke a secure link
 */
export function useRevokeSecureLink() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (uuid: string) => secureLinkApi.revoke(uuid),
    onSuccess: () => {
      // Invalidate and refetch secure link lists
      queryClient.invalidateQueries({ queryKey: secureLinkKeys.lists() });
    },
  });
}

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Get secure link status
 */
export function getSecureLinkStatus(
  link: SecureLink
): 'active' | 'expired' | 'used' {
  if (link.isUsed) {
    return 'used';
  }

  const now = new Date();
  const expiresAt = new Date(link.expiresAt);

  if (expiresAt < now) {
    return 'expired';
  }

  return 'active';
}

/**
 * Calculate days until expiration
 */
export function getDaysUntilExpiration(expiresAt: string): number {
  const now = new Date();
  const expiry = new Date(expiresAt);
  const diffMs = expiry.getTime() - now.getTime();
  return Math.ceil(diffMs / (1000 * 60 * 60 * 24));
}

/**
 * Format expiration countdown
 */
export function formatExpirationCountdown(expiresAt: string): string {
  const days = getDaysUntilExpiration(expiresAt);

  if (days < 0) {
    return 'Expired';
  }

  if (days === 0) {
    return 'Expires today';
  }

  if (days === 1) {
    return '1 day left';
  }

  return `${days} days left`;
}

/**
 * Truncate UUID for display
 */
export function truncateUUID(uuid: string, length: number = 8): string {
  if (uuid.length <= length) {
    return uuid;
  }
  return `${uuid.substring(0, length)}...`;
}

/**
 * Generate full secure link URL
 */
export function getSecureLinkURL(uuid: string): string {
  const baseUrl = import.meta.env.VITE_BASE_URL || (typeof window !== 'undefined'
    ? window.location.origin
    : '');
  return `${baseUrl}/upload/${uuid}`;
}

/**
 * Copy text to clipboard
 */
export async function copyToClipboard(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch (error) {
    if (import.meta.env.DEV) console.error('Failed to copy to clipboard:', error);
    return false;
  }
}

export default useSecureLinks;
